import React, { Fragment, useState } from 'react';
import { Button, CircularProgress, Typography, TextField, IconButton, InputAdornment } from '@mui/material';
import { Box, Container, Stack, Grid } from '../imports/muiImport';
import { Helmet } from 'react-helmet-async';
import { GoogleLogin } from '@react-oauth/google';

import { useNavigate, useLocation } from '../imports/generalImport';
import { useDispatch } from 'react-redux';
import { fetchUserProfile } from '../store/userProfile/action';
import { registerUserWithGoogle, loginUser, changePassword, verifyOtp } from '../apiCalls/urlFunction';
import { userTokenSave, isValidJwt, getToken } from '../apiCalls/apiHandler';
import { actionNotifier } from '../components/tostify';
import { useAuth } from '../hooks/contexts/AuthContext';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import loginimage from '../assets/99giftimages/Loginpage1.jpeg';
import { jwtDecode } from "jwt-decode";
import OtpInput from "react18-input-otp";
import { Loading } from 'notiflix';


const AuthLayout = () => {
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verifyOTPKey, setVerifyOTPKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setIsLoggedIn, setAuthUser } = useAuth();

  const isSignup = location.pathname.endsWith('/signup') || location.pathname === '/signup';

  // Resume at NEW_PASSWORD if the user already has a pending credential needing a password
  const [userProcessStatus, setUserProcessStatus] = useState(() => {
    try {
      const raw = localStorage.getItem('99GIFT');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.token && isValidJwt(parsed.token)) {
          const decoded = jwtDecode(parsed.token);
          if (decoded?.passwordExists === false && !parsed?.passwordCompleted) {
            return { currentStep: "NEW_PASSWORD" };
          }
        }
      }
    } catch (_) { }
    return { currentStep: "LOGIN_OR_SIGNUP" };
  });

  Loading.init({
    svgColor: '#002865',
    messageColor: '#000',
    backgroundColor: 'rgba(255,255,255,0.8)',
    clickToClose: false,
  });

  // Form inputs state management
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [otp, setOtp] = useState("");
  const [newPasswordData, setNewPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (error) setError("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!formData.email?.trim() || !formData.password?.trim()) {
      setError("Please enter both email and password.");
      return;
    }

    try {
      setLoading(true);
      Loading.hourglass('Please wait...');
      setError("");

      const response = await loginUser({
        email: formData.email.trim(),
        password: formData.password
      });

      if (response?.status) {
        setUserProcessStatus({
          currentStep: "OTP",
        });
        setVerifyOTPKey(response?.data);
      } else {
        setError(response?.message || "Invalid Email Or Password");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Invalid Email Or Password"
      );
    } finally {
      Loading.remove();
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      actionNotifier.error('Google authentication failed.');
      return;
    }

    setGoogleLoading(true);
    try {
      const payload = {
        idToken: credentialResponse.credential,
      };

      const response = await registerUserWithGoogle(payload);

      if (response?.status && response?.data) {
        if (typeof response.data !== 'string') {
          actionNotifier.error('Invalid response received from authentication server');
          return;
        }

        let decodedPayload;
        try {
          decodedPayload = jwtDecode(response.data);
        } catch (decodeErr) {

          actionNotifier.error('Invalid session token received from server');
          return;
        }

        if (decodedPayload?.passwordExists) {
          // User already has a password -> complete authentication and redirect to /
          const tokenSaved = userTokenSave(response.data, { passwordCompleted: true });

          if (tokenSaved) {
            setIsLoggedIn(true);
            setAuthUser({ token: response.data });
            try {
              dispatch(fetchUserProfile());
            } catch (_) {}
            window.location.href = '/';
          } else {
            actionNotifier.error('Failed to save session. Please try again.');
          }
        } else {
          // User needs to create a password (new user or user without password)
          // Preserve token in localStorage so changePassword can authorize
          userTokenSave(response.data, { passwordCompleted: false });
          setUserProcessStatus({ currentStep: "NEW_PASSWORD" });
          // actionNotifier.info('Please set a password to complete your account setup.', {
          //   position: 'top-left',
          //   autoClose: 3000,
          // });
        }
      } else {
        actionNotifier.error(response?.message || 'Google authentication failed', {
          position: 'top-left',
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error('Google auth error:', err);
      actionNotifier.error(
        err?.response?.data?.message || err?.message || 'Something went wrong during Google authentication',
        { position: 'top-left', autoClose: 3000 }
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setGoogleLoading(false);
    actionNotifier.error(
      'Google authentication was cancelled or failed',
      { position: 'top-left', autoClose: 2000 }
    );
  };

  const FormDesign = () => {
    return (
      <Fragment>
        {/* Header */}
        <Stack
          spacing={1.5}
          sx={{
            mb: { xs: 3, sm: 3.5 },
            width: '100%',
            alignItems: 'flex-start'
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: '#000',
              fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
              fontFamily: "Be Vietnam Pro",
              letterSpacing: '-0.5px',
            }}
          >
            {isSignup ? 'Create Account' : 'Welcome Back'}
          </Typography>

          <Typography
            sx={{
              fontWeight: 400,
              color: '#666666',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontFamily: "Be Vietnam Pro",
              lineHeight: 1.5,
            }}
          >
            {isSignup
              ? 'Sign up and start sending joy with eGifts cards'
              : 'Login to explore and send joy with eGifts cards'}
          </Typography>
        </Stack>

        {/* Google Login */}
        <Stack sx={{ position: 'relative', width: '100%', mb: 2 }}>
          {googleLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                borderRadius: '12px',
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme="outline"
            size="large"
            text={isSignup ? 'signup_with' : 'signin_with'}
            width="100%"
            logo_alignment="center"
            shape="pill"
          />
        </Stack>

        {!isSignup && (
          <Fragment>
            {/* Divider */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1.5,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e8e8e8',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  mx: 2,
                  color: '#999999',
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                or continue with
              </Typography>
            </Box>
            {error && (
              <Typography
                sx={{
                  mt: 1.5,
                  mb: 1.5,
                  m: 'auto',
                  fontFamily: "Be Vietnam Pro",
                  fontWeight: 500,
                  color: "error.main",
                  textAlign: 'center',
                  width: '100%'
                }}
              >
                {error}
              </Typography>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleFormSubmit} sx={{ width: '100%' }}>
              <Stack spacing={2}>
                {/* Email Field */}
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      mb: 0.75,
                      textAlign: 'left',
                      color: '#000',
                      fontFamily: "Be Vietnam Pro",
                      fontSize: '1rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    variant="outlined"
                    type="email"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ pl: 0.5, pr: 0.5 }}>
                          <Mail size={18} color="#999" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f5f6f8',
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f0f1f4',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 0 0 3px rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          border: '1px solid transparent',
                          transition: 'all 0.2s ease',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d0d0d0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#000000',
                          borderWidth: '1px',
                        },
                        '& input': {
                          py: 1.7,
                          fontSize: '0.9rem',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Password Field */}
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      mb: 0.75,
                      textAlign: 'left',
                      color: '#000',
                      fontFamily: "Be Vietnam Pro",
                      fontSize: '1rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    Password
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ pl: 0.5, pr: 0.5 }}>
                          <Lock size={18} color="#999" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'rgba(250, 84, 28, 0.05)' },
                            }}
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f5f6f8',
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f0f1f4',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 0 0 3px rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          border: '1px solid transparent',
                          transition: 'all 0.2s ease',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d0d0d0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#000000',
                          borderWidth: '1px',
                        },
                        '& input': {
                          py: 1.7,
                          fontSize: '0.9rem',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: '#ffffff',
                    borderRadius: '10px',
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1.2rem',
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    mt: 0.5,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                      thickness={5}
                    />
                  ) : (
                    'Login'
                  )}
                </Button>
              </Stack>
            </Box>
          </Fragment>
        )}

        {/* Toggle between Login and Signup */}
        <Box sx={{ mt: 2.5, textAlign: 'center', width: '100%' }}>
          <Typography
            variant="body2"
            sx={{
              color: '#666666',
              fontFamily: "Be Vietnam Pro",
              fontSize: '0.9rem',
            }}
          >
            {isSignup ? (
              <>
                Already have an account?{' '}
                <Typography
                  component="span"
                  onClick={() => {
                    setError("");
                    navigate('/login');
                  }}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Sign In
                </Typography>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <Typography
                  component="span"
                  onClick={() => {
                    setError("");
                    navigate('/signup');
                  }}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Create Account
                </Typography>
              </>
            )}
          </Typography>
        </Box>
      </Fragment>
    );
  };

  const OTPDesign = () => {
    const handleVerifyOtp = async (otpValue) => {
      if (!verifyOTPKey) {
        setOtpError("Verification session expired. Please log in again.");
        return;
      }

      try {
        setLoading(true);
        Loading.hourglass('Verifying OTP...');
        setOtpError("");

        const res = await verifyOtp({
          key: verifyOTPKey,
          otp: otpValue,
        });

        if (res?.status && res?.data) {
          if (typeof res.data !== 'string') {
            setOtpError("Invalid session received from server.");
            return;
          }

          let decodedPayload;
          try {
            decodedPayload = jwtDecode(res.data);
          } catch (err) {

            setOtpError("Invalid session token received from server.");
            return;
          }

          if (decodedPayload?.passwordExists) {
            const tokenSaved = userTokenSave(res.data, { passwordCompleted: true });

            if (tokenSaved) {
              setIsLoggedIn(true);
              setAuthUser({ token: res.data });
              try {
                dispatch(fetchUserProfile());
              } catch (_) {}
              window.location.href = '/';
            } else {
              setOtpError("Failed to save login session. Please try again.");
            }
          } else {
            // User does not have a password yet (passwordExists === false)
            userTokenSave(res.data, { passwordCompleted: false });
            setUserProcessStatus({ currentStep: "NEW_PASSWORD" });
            // actionNotifier.info("Please set a password to complete your account setup.", {
            //   position: "top-left",
            //   autoClose: 3000,
            // });
          }
        } else {
          setOtpError(res?.message || "Invalid OTP. Please check and try again.");
        }
      } catch (err) {
        setOtpError(
          err?.response?.data?.message ||
          err?.message ||
          "Invalid OTP. Please check and try again."
        );
      } finally {
        Loading.remove();
        setLoading(false);
      }
    };

    const handleOtpSubmit = (e) => {
      e.preventDefault();
      if (loading) return;
      const otpValue = otp;

      if (!otpValue || otpValue.length < 6) {
        // actionNotifier.error('Please enter the full 6-digit OTP');
        return;
      }

      handleVerifyOtp(otpValue);
    };

    return (
      <Fragment>
        <Stack spacing={1.5} sx={{ mb: { xs: 3, sm: 3.5 }, width: '100%', alignItems: 'flex-start' }}>
          <Typography sx={{ fontWeight: 700, color: '#000', fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }, fontFamily: "Be Vietnam Pro", letterSpacing: '-0.5px' }}>
            Verify OTP
          </Typography>
          <Typography
            sx={{
              color: otpError ? "error.main" : "#666666",
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontFamily: "Be Vietnam Pro",
              lineHeight: 1.5,
              fontWeight: otpError ? 600 : 400
            }}
          >
            {otpError
              ? otpError
              : "Please enter the 6-digit code sent to your email."}
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleOtpSubmit} sx={{ width: '100%' }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                isInputNum
                shouldAutoFocus
                separator={<span style={{ width: "8px" }} />}
                inputStyle={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "10px",
                  border: "1px solid #d0d5dd",
                  backgroundColor: "#f5f6f8",
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "#000",
                  textAlign: "center",
                  outline: "none",
                }}
                focusStyle={{
                  border: "1px solid #000",
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || otp?.length !== 6}
              sx={{
                backgroundColor: 'primary.main',
                color: '#ffffff',
                borderRadius: '10px',
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1.2rem',
                boxShadow: 'none',
                mt: 1,
                '&:hover': { backgroundColor: 'primary.main', transform: 'translateY(-1px)' }
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Verify OTP'
              )}
            </Button>
          </Stack>
        </Box>
      </Fragment>
    );
  };

  const NewPasswordDesign = () => {
    const handleNewPasswordChange = (e) => {
      const { name, value } = e.target;
      setNewPasswordData(prev => ({ ...prev, [name]: value }));
      if (passwordError) setPasswordError("");
    };

    const handleNewPasswordSubmit = async (e) => {
      e.preventDefault();
      if (passwordLoading) return;

      setPasswordError("");

      const password = newPasswordData.password?.trim();
      const confirmPassword = newPasswordData.confirmPassword?.trim();

      if (!password) {
        setPasswordError("Password is required.");
        return;
      }

      if (password.length < 6) {
        setPasswordError("Password must be at least 6 characters.");
        return;
      }

      if (!confirmPassword) {
        setPasswordError("Please confirm your password.");
        return;
      }

      if (password !== confirmPassword) {
        setPasswordError("Passwords do not match.");
        return;
      }

      try {
        setPasswordLoading(true);
        Loading.hourglass('Setting new password...');

        const response = await changePassword({
          password: String(password)
        });

        if (response?.status) {
          // If the backend returns a new JWT token in response.data, save it
          const potentialToken = typeof response?.data === 'string' ? response.data : response?.data?.token;
          if (potentialToken && isValidJwt(potentialToken)) {
            userTokenSave(potentialToken, { passwordCompleted: true });
            setAuthUser({ token: potentialToken });
          } else {
            // Otherwise finalize the existing saved session with passwordCompleted: true
            const currentToken = getToken();
            if (currentToken) {
              userTokenSave(currentToken, { passwordCompleted: true });
              setAuthUser({ token: currentToken });
            }
          }

          setIsLoggedIn(true);
          try {
            dispatch(fetchUserProfile());
          } catch (_) {}
          actionNotifier.success(response?.message || "Password set successfully!", {
            position: "top-left",
            autoClose: 2000,
          });
          window.location.href = '/';
        } else {
          setPasswordError(response?.message || "Failed to set password. Please try again.");
          actionNotifier.error(response?.message || "Failed to set password.", {
            position: "top-left",
            autoClose: 3000,
          });
        }
      } catch (err) {
        const errorMsg =
          err?.response?.data?.message ||
          err?.message ||
          "Please try again - Change Password!";
        setPasswordError(errorMsg);
        actionNotifier.error(errorMsg, {
          position: "top-left",
          autoClose: 3000,
        });
      } finally {
        Loading.remove();
        setPasswordLoading(false);
      }
    };

    return (
      <Fragment>
        <Stack spacing={1.5} sx={{ mb: { xs: 3, sm: 3.5 }, width: '100%', alignItems: 'flex-start' }}>
          <Typography sx={{ fontWeight: 700, color: '#000', fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }, fontFamily: "Be Vietnam Pro", letterSpacing: '-0.5px' }}>
            Set New Password
          </Typography>
          <Typography
            sx={{
              fontWeight: passwordError ? 600 : 400,
              color: passwordError ? 'error.main' : '#666666',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontFamily: "Be Vietnam Pro",
              lineHeight: 1.5
            }}
          >
            {passwordError || "Please enter your new password below to secure your account."}
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleNewPasswordSubmit} sx={{ width: '100%' }}>
          <Stack spacing={2}>
            {/* New Password Field */}
            <Box>
              <Typography sx={{ fontWeight: 500, mb: 0.75, textAlign: 'left', color: '#000', fontFamily: "Be Vietnam Pro", fontSize: '1rem' }}>
                New Password
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter new password"
                name="password"
                value={newPasswordData.password}
                onChange={handleNewPasswordChange}
                type={showNewPassword ? 'text' : 'password'}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={{ pl: 0.5, pr: 0.5 }}><Lock size={18} color="#999" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" sx={{ color: 'primary.main' }}>
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f6f8',
                    borderRadius: '10px',
                    '&.Mui-focused fieldset': { borderColor: '#000000', borderWidth: '1px' }
                  }
                }}
              />
            </Box>

            {/* Confirm Password Field */}
            <Box>
              <Typography sx={{ fontWeight: 500, mb: 0.75, textAlign: 'left', color: '#000', fontFamily: "Be Vietnam Pro", fontSize: '1rem' }}>
                Confirm Password
              </Typography>
              <TextField
                fullWidth
                placeholder="Confirm new password"
                name="confirmPassword"
                value={newPasswordData.confirmPassword}
                onChange={handleNewPasswordChange}
                type={showConfirmPassword ? 'text' : 'password'}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={{ pl: 0.5, pr: 0.5 }}><Lock size={18} color="#999" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: 'primary.main' }}>
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f6f8',
                    borderRadius: '10px',
                    '&.Mui-focused fieldset': { borderColor: '#000000', borderWidth: '1px' }
                  }
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={passwordLoading}
              sx={{
                backgroundColor: 'primary.main',
                color: '#ffffff',
                borderRadius: '10px',
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1.2rem',
                boxShadow: 'none',
                mt: 1,
                '&:hover': { backgroundColor: 'primary.main', transform: 'translateY(-1px)' }
              }}
            >
              {passwordLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Set Password'
              )}
            </Button>
          </Stack>
        </Box>
      </Fragment>
    );
  };

  const renderDesignHandler = () => {
    switch (userProcessStatus.currentStep) {
      case "LOGIN_OR_SIGNUP":
        return FormDesign();
      case "OTP":
        return OTPDesign();
      case "NEW_PASSWORD":
        return NewPasswordDesign();
      default:
        return FormDesign();
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#fafafa',
        position: 'relative',
        p: {
          lg: 1,
        }
      }}
    >
      <Helmet>
        <title>{isSignup ? 'Signup - 99gift.ae' : 'Login - 99gift.ae'}</title>
        <meta property="og:title" content={isSignup ? 'Signup - 99gift.ae' : 'Login - 99gift.ae'} />
      </Helmet>

      <Grid container
        sx={{
          width: '100%',
          flex: 1,
          backgroundColor: 'transparent',
        }}
      >
        {/* Left Side: Forms */}
        <Grid item xs={12} md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: '#ffffff',
            height: '100%',
            overflowY: 'auto',
            p: { xs: 2, sm: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              maxWidth: '400px',
              width: '100%',
              mx: 'auto',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="https://www.99gift.in/static/media/logo.84c3804906626862f8f077a5d0acd071.svg"
              alt="99gift.ae"
              onClick={() => navigate("/")}
              sx={{
                height: { xs: 55, sm: 65, md: 75 },
                objectFit: "contain",
                mb: { xs: 2.5, sm: 3.5 },
                alignSelf: "center",
                cursor: "pointer",
              }}
            />
            {renderDesignHandler()}

          </Box>
        </Grid>

        {/* Right Side: Image */}
        <Grid item xs={12} md={6}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            height: '100%',
            py: {
              md: 2,
            }
          }}
        >
          <Box
            component="img"
            src={loginimage}
            alt="Auth Visual"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: 4,
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default AuthLayout;
